'use client'

import appVariables from '@/config'
import { graphql } from '@/generated/gql'
import { useMutation } from '@apollo/client'
import { getCloudflareImageUrl } from '@fusion/media/cloudflare'
import { Close, StarRateRounded } from '@mui/icons-material'
import {
  Box,
  Button,
  CardMedia,
  Dialog,
  IconButton,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import NoImageAvailable from '@public/images/no-image-available.png'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { CanteenRatingItem } from './types'

const FEEDBACK_MAX_LENGTH = 500

const reviewCcOrderItemMutation = graphql(`
  mutation CanteenRatingBanner_ReviewCCOrderItem($input: ReviewCCOrderItemInput!) {
    reviewCCOrderItem(input: $input)
  }
`)

type Props = {
  item: CanteenRatingItem | null
  onClose: () => void
  onRated: (itemId: number) => void
  onError: () => void
}

const RatingModal: React.FC<Props> = ({ item, onClose, onRated, onError }) => {
  const { t } = useTranslation()
  const [rating, setRating] = useState(0)
  const [feedback, setFeedback] = useState('')
  const [runReview, { loading }] = useMutation(reviewCcOrderItemMutation)

  const imageUrl = item?.imageUrl
    ? getCloudflareImageUrl({
        enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
        src: item.imageUrl,
        width: 900,
        quality: 80,
      })
    : NoImageAvailable.src

  const reset = () => {
    setRating(0)
    setFeedback('')
  }

  const handleClose = () => {
    if (loading) return
    reset()
    onClose()
  }

  const handleSubmit = async () => {
    if (!item || rating === 0) return

    try {
      await runReview({
        variables: {
          input: {
            ccOrderItemId: item.id,
            rating,
            ...(feedback.trim() && { feedback: feedback.trim() }),
          },
        },
      })
      reset()
      onRated(item.id)
    } catch (err) {
      // Already rated in another tab; the row is done either way.
      if (String(err).includes('already been rated')) {
        reset()
        onRated(item.id)
        return
      }
      // Leave the typed feedback in place so a retry does not lose it.
      onError()
    }
  }

  return (
    <Dialog
      open={Boolean(item)}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, p: 1, overflowY: 'auto' } } }}>
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Typography variant="h6" fontWeight={700} data-cy="cc-review-modal">
            {t('dashboard:canteenRating.modalTitle')}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label={t('common:cancel')}
            sx={{ color: 'text.secondary' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <CardMedia
          sx={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 2, mt: 1 }}
          image={imageUrl}
          title={item?.name ?? ''}
          data-cy="cc-review-modal-image"
        />

        <Typography variant="body3" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {item ? `${item.name} · ${item.vendorName}` : ''}
        </Typography>

        <Stack spacing={2} mt={1.5}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontSize: 13, letterSpacing: 1 }}>
            {t('dashboard:canteenRating.ratingQuestion')}
          </Typography>
          <Stack spacing={2} alignItems="center">
            <Rating
              onChange={(_, newRating) => setRating(newRating ?? 0)}
              value={rating}
              max={5}
              data-cy="cc-review-stars"
              icon={<StarRateRounded fontSize="large" />}
              emptyIcon={<StarRateRounded fontSize="large" sx={{ color: 'action.disabled' }} />}
            />
            <TextField
              placeholder={t('dashboard:cateringRating.feedbackPlaceholder')}
              slotProps={{ htmlInput: { maxLength: FEEDBACK_MAX_LENGTH } }}
              multiline
              fullWidth
              minRows={3}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              data-cy="cc-review-textarea"
              sx={{ '& .MuiInputBase-input': { fontSize: 14 } }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {t('dashboard:cateringRating.cannotEdit')}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="right" mt={2}>
          <Button size="small" variant="outlined" onClick={handleClose} disabled={loading}>
            {t('common:cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={rating === 0 || loading}
            data-cy="cc-review-submit">
            {t('dashboard:cateringRating.submit')}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  )
}

export default RatingModal
